import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { AccountType } from '../lib/supabase';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';

const countryCodes = [
  { code: '+967', country: 'اليمن' },
  { code: '+249', country: 'السودان' },
  { code: '+20', country: 'مصر' },
  { code: '+966', country: 'السعودية' },
];

interface FormData {
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
  address: string;
  accountType: AccountType;
  referenceSmartKey: string;
  shopName?: string;
  businessType?: string;
}

function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+967');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    address: '',
    accountType: 'customer',
    referenceSmartKey: '',
    shopName: '',
    businessType: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToPrivacyPolicy) {
      setError('يجب الموافقة على سياسة الخصوصية للمتابعة');
      return;
    }
    
    setError(null);
    setLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('كلمات المرور غير متطابقة');
      }

      if (formData.accountType === 'shop_owner' && (!formData.shopName || !formData.businessType)) {
        throw new Error('اسم المتجر ونوع النشاط مطلوبان لأصحاب المتاجر');
      }

      if (recoveryEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recoveryEmail)) {
        throw new Error('البريد الإلكتروني غير صالح');
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: `${formData.phone}@ajel.life`,
        password: formData.password,
        phone: countryCode + formData.phone,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('لم يتم إنشاء المستخدم');

      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          username: formData.username,
          phone: formData.phone,
          country_code: countryCode,
          date_of_birth: formData.dateOfBirth,
          address: formData.address,
          account_type: formData.accountType,
          reference_smart_key: formData.referenceSmartKey || null,
          shop_name: formData.accountType === 'shop_owner' ? formData.shopName : null,
          business_type: formData.accountType === 'shop_owner' ? formData.businessType : null,
          assistant_vendor_smart_key: formData.accountType === 'shop_assistant' ? formData.referenceSmartKey : null,
          assistant_status: formData.accountType === 'shop_assistant' ? 'not_active' : null,
          recovery_email: recoveryEmail || null
        });

      if (profileError) {
        await supabase.auth.signOut();
        throw profileError;
      }

      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء التسجيل');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6 flex flex-col items-center justify-center">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">إنشاء حساب</h1>
          <p className="text-gray-600">أدخل بياناتك للبدء</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <input
              type="text"
              name="username"
              placeholder="اسم المستخدم"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 bg-white"
              required
            >
              {countryCodes.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.country} ({country.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <input
              type="tel"
              name="phone"
              placeholder="رقم الهاتف"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right"
              required
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="كلمة المرور"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="تأكيد كلمة المرور"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 text-right pl-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div>
            <input
              type="date"
              name="dateOfBirth"
              placeholder="تاريخ الميلاد"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <input
              type="text"
              name="address"
              placeholder="العنوان"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <select 
              name="accountType"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 bg-white"
              value={formData.accountType}
              onChange={handleChange}
              required
            >
              <option value="customer">عميل</option>
              <option value="shop_owner">صاحب متجر</option>
              <option value="shop_assistant">مساعد متجر</option>
            </select>
          </div>

          <div>
            <input
              type="text"
              name="referenceSmartKey"
              placeholder="الرمز الذكي لمن عرفك على النظام (اختياري)"
              value={formData.referenceSmartKey}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
              maxLength={6}
            />
          </div>

          {formData.accountType === 'shop_assistant' && (
            <div>
              <input
                type="text"
                name="referenceSmartKey"
                placeholder="الرمز الذكي لصاحب المتجر"
                value={formData.referenceSmartKey}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
                required
              />
            </div>
          )}

          {formData.accountType === 'shop_owner' && (
            <>
              <div>
                <input
                  type="text"
                  name="shopName"
                  placeholder="اسم المتجر"
                  value={formData.shopName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  name="businessType"
                  placeholder="نوع النشاط التجاري"
                  value={formData.businessType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
            </>
          )}

          <div>
            <input
              type="email"
              placeholder="البريد الإلكتروني لاستعادة كلمة المرور (اختياري)"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="privacyPolicy"
              checked={agreedToPrivacyPolicy}
              onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
              className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="privacyPolicy" className="text-sm text-gray-600">
              أوافق على{' '}
              <button
                type="button"
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-blue-500 hover:text-blue-600"
              >
                سياسة الخصوصية
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !agreedToPrivacyPolicy}
            className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'جاري إنشاء الحساب...' : 'تسجيل'}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-gray-500">
          لديك حساب بالفعل؟{' '}
          <button 
            onClick={() => navigate('/')}
            className="text-blue-500 hover:text-blue-600"
          >
            تسجيل الدخول
          </button>
        </p>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">سياسة الخصوصية</h2>
              <button 
                onClick={() => setShowPrivacyPolicy(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="prose prose-sm max-w-none">
              <p className="text-sm text-gray-600 mb-4">تاريخ السريان: 01/02/2025</p>

              <p className="mb-4">
                Ajel.life ("نحن" أو "لنا") ملتزمون بحماية خصوصيتك. توضح سياسة الخصوصية هذه كيفية جمع واستخدام والكشف عن وحماية معلوماتك عند استخدام تطبيق الويب الخاص بنا ("التطبيق").
              </p>

              <h3 className="text-lg font-semibold mb-2">المعلومات التي نجمعها</h3>
              <p className="mb-4">قد نجمع المعلومات التالية منك:</p>
              <ul className="list-disc list-inside mb-4">
                <li>عنوان البريد الإلكتروني</li>
                <li>رقم الهاتف المحمول</li>
                <li>اسم المستخدم</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">كيف نستخدم معلوماتك</h3>
              <p className="mb-2">نستخدم المعلومات التي نجمعها للأغراض التالية:</p>
              <ul className="list-disc list-inside mb-4">
                <li>إنشاء وإدارة حسابات المستخدمين</li>
                <li>التواصل معك، بما في ذلك إرسال التحديثات والإشعارات</li>
                <li>تحسين خدماتنا وتجربة المستخدم</li>
                <li>تقديم دعم العملاء</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">مشاركة المعلومات</h3>
              <p className="mb-2">
                نحن لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع أطراف ثالثة إلا في الحالات التالية:
              </p>
              <ul className="list-disc list-inside mb-4">
                <li>بموافقتك</li>
                <li>لأسباب قانونية، مثل الامتثال للالتزامات القانونية أو الاستجابة للطلبات القانونية أو حماية حقوقنا</li>
                <li>مع مقدمي الخدمات الذين يقدمون خدمات نيابة عنا بموجب اتفاقيات سرية صارمة</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">أمن البيانات</h3>
              <p className="mb-4">
                نقوم بتنفيذ تدابير تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو الاستخدام أو الكشف أو التعديل أو التدمير.
              </p>

              <h3 className="text-lg font-semibold mb-2">حقوقك</h3>
              <p className="mb-2">لديك الحق في:</p>
              <ul className="list-disc list-inside mb-4">
                <li>الوصول إلى معلوماتك الشخصية أو تحديثها أو حذفها</li>
                <li>سحب موافقتك في أي وقت (عند الاقتضاء)</li>
                <li>الاتصال بنا بخصوص أي مخاوف تتعلق بالخصوصية</li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">التغييرات في سياسة الخصوصية</h3>
              <p className="mb-4">
                قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر أي تغييرات على هذه الصفحة مع تاريخ سريان محدث.
              </p>

              <h3 className="text-lg font-semibold mb-2">اتصل بنا</h3>
              <p className="mb-4">
                إذا كان لديك أي أسئلة أو مخاوف بشأن سياسة الخصوصية هذه، يرجى الاتصال بنا على:
              </p>
              <p>البريد الإلكتروني: ajel.life@gmail.com</p>

              <p className="mt-6 text-sm text-gray-500">Ajel.life</p>
              <p className="text-sm text-gray-500">01/02/2025</p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="px-4 py-2 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;